import { keepPreviousData, queryOptions, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { api, regionTokenFor } from "./index";
import type { QueryIr, QueryResponse, ResultFor } from "./ir";
import type { EnvironmentName, InsightListParams, RegionToken } from "./schemas";

export const keys = {
  me: ["me"] as const,
  regions: ["regions"] as const,
  projects: (orgId: string) => ["orgs", orgId, "projects"] as const,
  project: (id: string) => ["projects", id] as const,
  keys: (id: string) => ["projects", id, "keys"] as const,
  tokens: ["tokens"] as const,
  events: (projectId: string, env: EnvironmentName) => ["region", projectId, env, "events"] as const,
  properties: (projectId: string, env: EnvironmentName) => ["region", projectId, env, "properties"] as const,
  query: (projectId: string, env: EnvironmentName, ir: QueryIr) => ["region", projectId, env, "query", ir] as const,
  // Wave 2 — configuration (control plane)
  sessions: ["sessions"] as const,
  members: (orgId: string) => ["orgs", orgId, "members"] as const,
  insights: (projectId: string) => ["projects", projectId, "insights"] as const,
  insightList: (projectId: string, params: InsightListParams) => ["projects", projectId, "insights", "list", params] as const,
  insight: (projectId: string, id: string) => ["projects", projectId, "insights", "one", id] as const,
  dashboards: (projectId: string) => ["projects", projectId, "dashboards"] as const,
  dashboard: (projectId: string, id: string) => ["projects", projectId, "dashboards", "one", id] as const,
  cohorts: (projectId: string) => ["projects", projectId, "cohorts"] as const,
  cohort: (projectId: string, id: string) => ["projects", projectId, "cohorts", "one", id] as const,
  catalogEvents: (projectId: string) => ["projects", projectId, "catalog", "events"] as const,
  catalogProperties: (projectId: string) => ["projects", projectId, "catalog", "properties"] as const,
  // Wave 2 — region
  regionToken: (projectId: string, env: EnvironmentName) => ["region", projectId, env, "token"] as const,
  cohortSize: (projectId: string, env: EnvironmentName, id: string) => ["region", projectId, env, "cohort-size", id] as const,
  persons: (projectId: string, env: EnvironmentName, search: string) => ["region", projectId, env, "persons", search] as const,
  person: (projectId: string, env: EnvironmentName, id: string) => ["region", projectId, env, "person", id] as const,
  personEvents: (projectId: string, env: EnvironmentName, id: string) => ["region", projectId, env, "person", id, "events"] as const,
  identity: (projectId: string, env: EnvironmentName, id: string) => ["region", projectId, env, "person", id, "identity"] as const,
  groupTypes: (projectId: string, env: EnvironmentName) => ["region", projectId, env, "group-types"] as const,
  groups: (projectId: string, env: EnvironmentName, typeIndex: number, search: string) =>
    ["region", projectId, env, "groups", typeIndex, search] as const,
};

export const meQuery = queryOptions({ queryKey: keys.me, queryFn: () => api.me(), staleTime: 60_000 });
export const regionsQuery = queryOptions({ queryKey: keys.regions, queryFn: () => api.regions(), staleTime: Infinity });
export const projectsQuery = (orgId: string) =>
  queryOptions({ queryKey: keys.projects(orgId), queryFn: () => api.listProjects(orgId), staleTime: 30_000 });
export const projectQuery = (id: string) =>
  queryOptions({ queryKey: keys.project(id), queryFn: () => api.getProject(id), staleTime: 60_000 });
export const keysQuery = (id: string) => queryOptions({ queryKey: keys.keys(id), queryFn: () => api.listKeys(id) });
export const tokensQuery = queryOptions({ queryKey: keys.tokens, queryFn: () => api.listTokens() });

export function useEvents(projectId: string, env: EnvironmentName) {
  return useQuery({
    queryKey: keys.events(projectId, env),
    queryFn: async () => api.events(await regionTokenFor(projectId, env)),
    staleTime: 5 * 60_000,
  });
}

export function useProperties(projectId: string, env: EnvironmentName) {
  return useQuery({
    queryKey: keys.properties(projectId, env),
    queryFn: async () => api.properties(await regionTokenFor(projectId, env)),
    staleTime: 5 * 60_000,
  });
}

/** Runs an insight against the project's region. Keeps the previous result on screen while a new one loads. */
export function useInsight<Q extends QueryIr>(projectId: string, env: EnvironmentName, ir: Q | null) {
  return useQuery({
    queryKey: ir ? keys.query(projectId, env, ir) : ["region", projectId, env, "query", "idle"],
    enabled: ir !== null,
    queryFn: async ({ signal }) => {
      if (!ir) throw new Error("no query");
      const rt = await regionTokenFor(projectId, env);
      return (await api.query(rt, ir, signal)) as QueryResponse<ResultFor<Q>>;
    },
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    retry: 1,
  });
}

// ── Wave 2 ───────────────────────────────────────────────────────────────────

export const sessionsQuery = queryOptions({ queryKey: keys.sessions, queryFn: () => api.listSessions() });
export const membersQuery = (orgId: string) =>
  queryOptions({ queryKey: keys.members(orgId), queryFn: () => api.listMembers(orgId), staleTime: 5 * 60_000 });

export const insightsQuery = (projectId: string, params: InsightListParams = {}) =>
  queryOptions({
    queryKey: keys.insightList(projectId, params),
    queryFn: () => api.listInsights(projectId, params),
    placeholderData: keepPreviousData,
  });
export const insightQuery = (projectId: string, id: string) =>
  queryOptions({ queryKey: keys.insight(projectId, id), queryFn: () => api.getInsight(projectId, id), staleTime: 30_000 });
export const dashboardsQuery = (projectId: string) =>
  queryOptions({ queryKey: keys.dashboards(projectId), queryFn: () => api.listDashboards(projectId), staleTime: 30_000 });
export const dashboardQuery = (projectId: string, id: string) =>
  queryOptions({ queryKey: keys.dashboard(projectId, id), queryFn: () => api.getDashboard(projectId, id), staleTime: 30_000 });
export const cohortsQuery = (projectId: string) =>
  queryOptions({ queryKey: keys.cohorts(projectId), queryFn: () => api.listCohorts(projectId), staleTime: 30_000 });
export const cohortQuery = (projectId: string, id: string) =>
  queryOptions({ queryKey: keys.cohort(projectId, id), queryFn: () => api.getCohort(projectId, id) });
export const catalogEventsQuery = (projectId: string) =>
  queryOptions({ queryKey: keys.catalogEvents(projectId), queryFn: () => api.listCatalogEvents(projectId), staleTime: 60_000 });
export const catalogPropertiesQuery = (projectId: string) =>
  queryOptions({
    queryKey: keys.catalogProperties(projectId),
    queryFn: () => api.listCatalogProperties(projectId),
    staleTime: 60_000,
  });

/** Run a region call with a (cached) region token for the project + environment. */
export async function withRegion<T>(projectId: string, env: EnvironmentName, fn: (rt: RegionToken) => Promise<T>): Promise<T> {
  return fn(await regionTokenFor(projectId, env));
}

/** The region token's scopes decide region-side capabilities such as `sql.raw`. */
export function useRegionScopes(projectId: string, env: EnvironmentName) {
  return useQuery({
    queryKey: keys.regionToken(projectId, env),
    queryFn: async () => (await regionTokenFor(projectId, env)).scopes,
    staleTime: 60_000,
  });
}

export function useCohortSize(projectId: string, env: EnvironmentName, cohortId: string, enabled = true) {
  return useQuery({
    queryKey: keys.cohortSize(projectId, env, cohortId),
    queryFn: () => api.cohortSize(projectId, env, cohortId),
    enabled,
    staleTime: 60_000,
  });
}

export function usePersons(projectId: string, env: EnvironmentName, search: string) {
  return useInfiniteQuery({
    queryKey: keys.persons(projectId, env, search),
    queryFn: ({ pageParam }) =>
      withRegion(projectId, env, (rt) => api.listPersons(rt, { search: search || undefined, cursor: pageParam ?? undefined })),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function usePerson(projectId: string, env: EnvironmentName, id: string) {
  return useQuery({
    queryKey: keys.person(projectId, env, id),
    queryFn: () => withRegion(projectId, env, (rt) => api.getPerson(rt, id)),
  });
}

export function usePersonEvents(projectId: string, env: EnvironmentName, id: string) {
  return useInfiniteQuery({
    queryKey: keys.personEvents(projectId, env, id),
    queryFn: ({ pageParam }) => withRegion(projectId, env, (rt) => api.personEvents(rt, id, pageParam ?? undefined)),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
  });
}

export function useIdentityGraph(projectId: string, env: EnvironmentName, id: string) {
  return useQuery({
    queryKey: keys.identity(projectId, env, id),
    queryFn: () => withRegion(projectId, env, (rt) => api.identityGraph(rt, id)),
  });
}

export function useGroupTypes(projectId: string, env: EnvironmentName) {
  return useQuery({
    queryKey: keys.groupTypes(projectId, env),
    queryFn: () => withRegion(projectId, env, (rt) => api.groupTypes(rt)),
    staleTime: 5 * 60_000,
  });
}

export function useGroups(projectId: string, env: EnvironmentName, typeIndex: number | null, search: string) {
  return useQuery({
    queryKey: keys.groups(projectId, env, typeIndex ?? -1, search),
    queryFn: () =>
      withRegion(projectId, env, (rt) => api.listGroups(rt, { typeIndex: typeIndex ?? 0, search: search || undefined })),
    enabled: typeIndex !== null,
    placeholderData: keepPreviousData,
  });
}
